// ═══════════════════════════════════════════════════════════════
// BLACKBOARD — Training Records Management
// Ported from Firebase TrainingRecords.jsx → Ignatius/Zustand
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  TextInput,
  Select,
  Button,
  Text,
  Group,
  Badge,
  Modal,
  Textarea,
  Table,
  ActionIcon,
  Stack,
  Loader,
  Center,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconSearch,
  IconPlus,
  IconFileExport,
  IconBook,
  IconFilter,
  IconEye,
  IconPencil,
  IconTrash,
  IconCalendar,
  IconCalendarOff,
  IconUser,
  IconClipboardList,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useBlackboardStore } from '../../store/blackboardStore';
import type { TrainingRecord, Training } from '../../store/blackboardStore';
import { generateCertCode } from './utils/certificateUtils';

// ── Status helpers ────────────────────────────────────────────

interface StatusInfo {
  label: string;
  color: string;
  key: 'compliant' | 'expired' | 'expiring';
}

function getStatusInfo(record: TrainingRecord, trainings: Training[]): StatusInfo {
  if (!record.completedDate) {
    return { label: 'Incomplete', color: '#dc2626', key: 'expired' };
  }

  const training = trainings.find(t => String(t.id) === record.trainingId);
  const freqDays = training?.frequencyDays ?? 0;

  // One-time training that is completed
  if (freqDays === 0 || !record.expiresDate) {
    return { label: 'Completed', color: '#16a34a', key: 'compliant' };
  }

  const now = new Date();
  const expires = new Date(record.expiresDate);
  const daysUntil = Math.ceil((expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (daysUntil <= 7) return { label: 'Expired', color: '#dc2626', key: 'expired' };
  if (daysUntil <= 30) return { label: 'Expiring', color: '#ea580c', key: 'expiring' };
  if (daysUntil <= 60) return { label: 'Expiring', color: '#d97706', key: 'expiring' };
  return { label: 'Compliant', color: '#16a34a', key: 'compliant' };
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '\u2014';
  return dayjs(d).format('MMM D, YYYY');
}

// ── Sorting ───────────────────────────────────────────────────

type SortDir = 'asc' | 'desc';

function sortRecords(
  data: TrainingRecord[],
  field: string | null,
  dir: SortDir,
  trainings: Training[],
): TrainingRecord[] {
  if (!field) return data;
  const sorted = [...data].sort((a, b) => {
    let aVal: string | number = '';
    let bVal: string | number = '';
    switch (field) {
      case 'student':
        aVal = (a.userName || '').toLowerCase();
        bVal = (b.userName || '').toLowerCase();
        break;
      case 'training':
        aVal = (a.trainingName || '').toLowerCase();
        bVal = (b.trainingName || '').toLowerCase();
        break;
      case 'completed':
        aVal = a.completedDate || '';
        bVal = b.completedDate || '';
        break;
      case 'expires':
        aVal = a.expiresDate || '';
        bVal = b.expiresDate || '';
        break;
      case 'status':
        aVal = getStatusInfo(a, trainings).label;
        bVal = getStatusInfo(b, trainings).label;
        break;
      default:
        return 0;
    }
    if (aVal < bVal) return -1;
    if (aVal > bVal) return 1;
    return 0;
  });
  return dir === 'desc' ? sorted.reverse() : sorted;
}

// ── Component ─────────────────────────────────────────────────

export default function TrainingRecords() {
  const {
    trainingRecords,
    trainings,
    users,
    certificates,
    loading,
    loadTrainingRecords,
    loadTrainings,
    loadUsers,
    loadCertificates,
    createTrainingRecord,
    updateTrainingRecord,
    deleteTrainingRecord,
    createCertificate,
  } = useBlackboardStore();

  // Filters
  const [search, setSearch] = useState('');
  const [studentFilter, setStudentFilter] = useState<string | null>(null);
  const [trainingFilter, setTrainingFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');

  // Sorting
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TrainingRecord | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formUserId, setFormUserId] = useState<string | null>(null);
  const [formTrainingId, setFormTrainingId] = useState<string | null>(null);
  const [formCompletedDate, setFormCompletedDate] = useState<string | null>(null);
  const [formExpiresDate, setFormExpiresDate] = useState<string | null>(null);
  const [formNotes, setFormNotes] = useState('');

  // View modal
  const [viewRecord, setViewRecord] = useState<TrainingRecord | null>(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<TrainingRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ── Data loading ──────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    await Promise.all([loadTrainingRecords(), loadTrainings(), loadUsers(), loadCertificates()]);
  }, [loadTrainingRecords, loadTrainings, loadUsers, loadCertificates]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ── Active data ───────────────────────────────────────────────

  const activeTrainings = useMemo(
    () => trainings.filter(t => t.active !== 'false' && t.active !== '0'),
    [trainings],
  );

  const activeUsers = useMemo(
    () => users.filter(u => u.active !== 'false' && u.active !== '0'),
    [users],
  );

  // ── Filters ───────────────────────────────────────────────────

  const filtered = useMemo(() => {
    return trainingRecords.filter(r => {
      if (search) {
        const q = search.toLowerCase();
        const name = (r.userName || '').toLowerCase();
        const tName = (r.trainingName || '').toLowerCase();
        if (!name.includes(q) && !tName.includes(q)) return false;
      }
      if (studentFilter && r.userId !== studentFilter) return false;
      if (trainingFilter && r.trainingId !== trainingFilter) return false;
      if (statusFilter !== 'all') {
        const status = getStatusInfo(r, trainings);
        if (statusFilter === 'compliant' && status.key !== 'compliant') return false;
        if (statusFilter === 'expiring' && status.key !== 'expiring') return false;
        if (statusFilter === 'expired' && status.key !== 'expired') return false;
      }
      return true;
    });
  }, [trainingRecords, search, studentFilter, trainingFilter, statusFilter, trainings]);

  const sortedFiltered = useMemo(
    () => sortRecords(filtered, sortField, sortDir, trainings),
    [filtered, sortField, sortDir, trainings],
  );

  // ── Select options ────────────────────────────────────────────

  const trainingOptions = useMemo(
    () => activeTrainings.map(t => ({ value: String(t.id), label: t.name })),
    [activeTrainings],
  );

  const userOptions = useMemo(
    () =>
      activeUsers.map(u => ({
        value: String(u.id),
        label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
      })),
    [activeUsers],
  );

  // ── Sorting toggle ────────────────────────────────────────────

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortTh = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <Table.Th
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => toggleSort(field)}
    >
      <Group gap={4} wrap="nowrap">
        {children}
        {sortField === field && (
          <Text size="xs" c="dimmed">{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</Text>
        )}
      </Group>
    </Table.Th>
  );

  // ── Form helpers ──────────────────────────────────────────────

  const resetForm = () => {
    setFormUserId(null);
    setFormTrainingId(null);
    setFormCompletedDate(null);
    setFormExpiresDate(null);
    setFormNotes('');
    setEditing(null);
  };

  const openAdd = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEdit = (record: TrainingRecord) => {
    setEditing(record);
    setFormUserId(record.userId || null);
    setFormTrainingId(record.trainingId || null);
    setFormCompletedDate(record.completedDate || null);
    setFormExpiresDate(record.expiresDate || null);
    setFormNotes(record.notes || '');
    setModalOpen(true);
  };

  // Auto-calculate expiration when training or completion date changes
  const autoCalcExpiry = (trainingId: string | null, completedDate: string | null) => {
    if (!trainingId || !completedDate) return;
    const training = activeTrainings.find(t => String(t.id) === trainingId);
    if (!training || training.frequencyDays === 0) {
      setFormExpiresDate(null);
      return;
    }
    setFormExpiresDate(dayjs(completedDate).add(training.frequencyDays, 'day').format('YYYY-MM-DD'));
  };

  const handleTrainingChange = (val: string | null) => {
    setFormTrainingId(val);
    if (val && formCompletedDate) {
      autoCalcExpiry(val, formCompletedDate);
    }
  };

  const handleCompletedDateChange = (val: string | null) => {
    setFormCompletedDate(val);
    if (val && formTrainingId) {
      autoCalcExpiry(formTrainingId, val);
    }
  };

  // ── Save ──────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!formUserId || !formTrainingId || !formCompletedDate) {
      notifications.show({ title: 'Validation', message: 'Student, training, and completion date are required.', color: 'yellow' });
      return;
    }
    setSaving(true);

    const user = activeUsers.find(u => String(u.id) === formUserId);
    const training = activeTrainings.find(t => String(t.id) === formTrainingId);

    const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || '';
    const trainingName = training?.name || '';
    const completedStr = dayjs(formCompletedDate).format('YYYY-MM-DD');
    const expiresStr = formExpiresDate ? dayjs(formExpiresDate).format('YYYY-MM-DD') : '';

    // Compute status
    let status = 'compliant';
    if (formExpiresDate) {
      const daysUntil = dayjs(formExpiresDate).diff(dayjs(), 'day');
      if (daysUntil <= 7) status = 'expired';
      else if (daysUntil <= 30) status = 'expiring';
    }

    try {
      if (editing) {
        await updateTrainingRecord(editing.id, {
          userId: formUserId,
          trainingId: formTrainingId,
          userName,
          trainingName,
          completedDate: completedStr,
          expiresDate: expiresStr,
          status,
          notes: formNotes.trim(),
        });
        notifications.show({ title: 'Success', message: 'Record updated successfully.', color: 'green' });
      } else {
        await createTrainingRecord({
          userId: formUserId,
          trainingId: formTrainingId,
          userName,
          trainingName,
          completedDate: completedStr,
          expiresDate: expiresStr,
          status,
          notes: formNotes.trim(),
          certificateId: '',
        });
        notifications.show({ title: 'Success', message: 'Record created successfully.', color: 'green' });
      }

      // Auto-generate certificate if completed and none exists yet
      if (completedStr) {
        const existingCert = certificates.find(
          c => c.userId === formUserId && String(c.trainingId) === formTrainingId
        );
        if (!existingCert) {
          try {
            const certCode = generateCertCode();
            await createCertificate({
              userId: formUserId,
              userName,
              trainingName,
              trainingId: formTrainingId,
              certCode,
              completedDate: completedStr,
              expiresDate: expiresStr,
            });
            notifications.show({ title: 'Certificate', message: `Certificate auto-generated: ${certCode}`, color: 'blue' });
          } catch { /* best effort */ }
        }
      }

      setModalOpen(false);
      resetForm();
    } catch (err) {
      notifications.show({ title: 'Error', message: `Failed to save record: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteTrainingRecord(deleteTarget.id);
      notifications.show({ title: 'Deleted', message: 'Record deleted.', color: 'green' });
      setDeleteTarget(null);
    } catch (err) {
      notifications.show({ title: 'Error', message: `Failed to delete record: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'red' });
    } finally {
      setDeleting(false);
    }
  };

  // ── CSV Export ────────────────────────────────────────────────

  const handleExport = () => {
    try {
      const headers = ['Student Name', 'Training', 'Completed Date', 'Expires Date', 'Status', 'Notes'];
      const rows = filtered.map(r => {
        const status = getStatusInfo(r, trainings);
        return [
          r.userName || '',
          r.trainingName || '',
          formatDate(r.completedDate),
          formatDate(r.expiresDate),
          status.label,
          (r.notes || '').replace(/"/g, '""'),
        ];
      });
      const csv = [headers, ...rows].map(row => row.map(c => `"${c}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `training-records-${dayjs().format('YYYY-MM-DD')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      notifications.show({ title: 'Exported', message: 'CSV export complete.', color: 'green' });
    } catch (err) {
      notifications.show({ title: 'Error', message: `Export failed: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'red' });
    }
  };

  // ── Render ────────────────────────────────────────────────────

  if (loading && trainingRecords.length === 0) {
    return (
      <Center h={400}>
        <Loader color="blue" />
      </Center>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Group justify="space-between" align="center" mb="lg">
        <Box>
          <Text size="10px" fw={700} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: 2 }}>
            Administration
          </Text>
          <Text size="xl" fw={700} c="#1a365d">
            Training Records
          </Text>
        </Box>
        <Group gap="xs">
          <Button
            variant="outline"
            color="blue"
            leftSection={<IconFileExport size={14} />}
            onClick={handleExport}
          >
            Export
          </Button>
          <Button
            color="blue"
            leftSection={<IconPlus size={14} />}
            onClick={openAdd}
          >
            Add Record
          </Button>
        </Group>
      </Group>

      {/* Filters */}
      <Paper p="sm" mb="lg" withBorder>
        <Group grow>
          <TextInput
            placeholder="Search by name or training..."
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={e => setSearch(e.currentTarget.value)}
          />
          <Select
            placeholder="All Students"
            data={userOptions}
            value={studentFilter}
            onChange={setStudentFilter}
            clearable
            searchable
            leftSection={<IconUser size={14} />}
          />
          <Select
            placeholder="All Trainings"
            data={trainingOptions}
            value={trainingFilter}
            onChange={setTrainingFilter}
            clearable
            searchable
            leftSection={<IconBook size={14} />}
          />
          <Select
            placeholder="All Statuses"
            data={[
              { value: 'all', label: 'All Statuses' },
              { value: 'compliant', label: 'Compliant' },
              { value: 'expiring', label: 'Expiring Soon' },
              { value: 'expired', label: 'Expired' },
            ]}
            value={statusFilter}
            onChange={v => setStatusFilter(v || 'all')}
            leftSection={<IconFilter size={14} />}
          />
        </Group>
      </Paper>

      {/* Summary badge */}
      <Group mb="sm">
        <Badge variant="light" color="blue" size="lg">
          {filtered.length} record{filtered.length !== 1 ? 's' : ''}
        </Badge>
      </Group>

      {/* Table */}
      <Paper p={0} withBorder style={{ overflow: 'auto' }}>
        {sortedFiltered.length === 0 ? (
          <Center p="xl">
            <Box style={{ textAlign: 'center' }}>
              <IconClipboardList size={32} color="#868e96" style={{ marginBottom: 8 }} />
              <Text c="dimmed" size="sm">
                No training records found.
              </Text>
            </Box>
          </Center>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <SortTh field="student">Student Name</SortTh>
                <SortTh field="training">Training</SortTh>
                <SortTh field="completed">Completed</SortTh>
                <SortTh field="expires">Expires</SortTh>
                <SortTh field="status">Status</SortTh>
                <Table.Th>Notes</Table.Th>
                <Table.Th style={{ width: 100 }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedFiltered.map(r => {
                const status = getStatusInfo(r, trainings);
                return (
                  <Table.Tr key={r.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>{r.userName || '\u2014'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{r.trainingName || '\u2014'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatDate(r.completedDate)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatDate(r.expiresDate)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        variant="light"
                        size="sm"
                        styles={{
                          root: {
                            backgroundColor: status.color + '18',
                            color: status.color,
                          },
                        }}
                      >
                        {status.label}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed" lineClamp={1} maw={150}>
                        {r.notes || '\u2014'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Tooltip label="View">
                          <ActionIcon variant="subtle" color="blue" size="sm" onClick={() => setViewRecord(r)}>
                            <IconEye size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Edit">
                          <ActionIcon variant="subtle" color="blue" size="sm" onClick={() => openEdit(r)}>
                            <IconPencil size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete">
                          <ActionIcon variant="subtle" color="red" size="sm" onClick={() => setDeleteTarget(r)}>
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      {/* ── Add/Edit Modal ──────────────────────────────────────────── */}
      <Modal
        opened={modalOpen}
        onClose={() => { setModalOpen(false); resetForm(); }}
        title={<Text fw={600}>{editing ? 'Edit Training Record' : 'Add Training Record'}</Text>}
        size="lg"
      >
        <Stack gap="sm">
          <Select
            label="Student"
            placeholder="Select a student"
            data={userOptions}
            value={formUserId}
            onChange={setFormUserId}
            searchable
            required
            leftSection={<IconUser size={14} />}
          />

          <Select
            label="Training"
            placeholder="Select a training"
            data={trainingOptions}
            value={formTrainingId}
            onChange={handleTrainingChange}
            searchable
            required
            leftSection={<IconBook size={14} />}
          />

          <Group grow>
            <DateInput
              label="Completion Date"
              placeholder="Select date"
              value={formCompletedDate}
              onChange={handleCompletedDateChange}
              required
              size="sm"
              leftSection={<IconCalendar size={14} />}
            />
            <DateInput
              label="Expiration Date"
              placeholder="Auto-calculated"
              value={formExpiresDate}
              onChange={setFormExpiresDate}
              size="sm"
              clearable
              leftSection={<IconCalendarOff size={14} />}
            />
          </Group>

          <Textarea
            label="Notes"
            placeholder="Optional notes..."
            minRows={2}
            value={formNotes}
            onChange={e => setFormNotes(e.currentTarget.value)}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => { setModalOpen(false); resetForm(); }}>
              Cancel
            </Button>
            <Button color="blue" onClick={handleSave} loading={saving}>
              {editing ? 'Update Record' : 'Create Record'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── View Detail Modal ───────────────────────────────────────── */}
      <Modal
        opened={!!viewRecord}
        onClose={() => setViewRecord(null)}
        title={<Text fw={600}>Record Details</Text>}
        size="md"
      >
        {viewRecord && (() => {
          const s = getStatusInfo(viewRecord, trainings);
          return (
            <Stack gap="sm">
              <Group>
                <Text size="sm" fw={500} w={120}>Student:</Text>
                <Text size="sm">{viewRecord.userName || '\u2014'}</Text>
              </Group>
              <Group>
                <Text size="sm" fw={500} w={120}>Training:</Text>
                <Text size="sm">{viewRecord.trainingName || '\u2014'}</Text>
              </Group>
              <Group>
                <Text size="sm" fw={500} w={120}>Completed:</Text>
                <Text size="sm">{formatDate(viewRecord.completedDate)}</Text>
              </Group>
              <Group>
                <Text size="sm" fw={500} w={120}>Expires:</Text>
                <Text size="sm">{formatDate(viewRecord.expiresDate)}</Text>
              </Group>
              <Group>
                <Text size="sm" fw={500} w={120}>Status:</Text>
                <Badge
                  variant="light"
                  size="sm"
                  styles={{ root: { backgroundColor: s.color + '18', color: s.color } }}
                >
                  {s.label}
                </Badge>
              </Group>
              {viewRecord.notes && (
                <Box>
                  <Text size="sm" fw={500} mb={2}>Notes:</Text>
                  <Text size="sm" c="dimmed">{viewRecord.notes}</Text>
                </Box>
              )}
              <Group justify="flex-end" mt="md">
                <Button variant="default" onClick={() => setViewRecord(null)}>
                  Close
                </Button>
              </Group>
            </Stack>
          );
        })()}
      </Modal>

      {/* ── Delete Confirmation Modal ───────────────────────────────── */}
      <Modal
        opened={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={<Text fw={600}>Delete Record</Text>}
        size="sm"
      >
        <Text size="sm" mb="lg">
          Are you sure you want to delete the training record for{' '}
          <Text span fw={600}>{deleteTarget?.userName}</Text>{' \u2014 '}
          <Text span fw={600}>{deleteTarget?.trainingName}</Text>?
          This action cannot be undone.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button color="red" onClick={handleDelete} loading={deleting}>
            Delete
          </Button>
        </Group>
      </Modal>
    </Box>
  );
}
